// front/src/main.js
'use strict'

import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { createHead } from '@vueuse/head'
import VueDOMPurifyHTML from 'vue-dompurify-html'
import App from './App.vue'
import i18n from './i18n'
import axios from 'axios'
import { setupAutoAdsManager } from './ads/autoAdsManager'
import './styles/base_theme.css'
import './styles/marketing_chrome.css'

// --- routes ---
import AccountSignup from './account/signup.vue'
import AccountSignin from './account/signin.vue'

const routes = [
  { path: '/signup', component: AccountSignup },
  { path: '/signin', component: AccountSignin },
  { path: '/logout', component: () => import(/* webpackChunkName: "logout" */ './account/logout.vue') },
  { path: '/forgot-password', component: () => import(/* webpackChunkName: "forgot-password" */ './account/forgot-password.vue') },
  { path: '/reset-password', component: () => import(/* webpackChunkName: "reset-password" */ './account/reset-password.vue') },
  { path: '/my-cards', component: () => import(/* webpackChunkName: "my-cards" */ './account/my-cards.vue') },
  { path: '/account', component: () => import(/* webpackChunkName: "account" */ './account/account.vue') },
  { path: '/card', component: () => import(/* webpackChunkName: "create-card" */ './create_card.vue') },
  { path: '/message/:occasion/:recipient/:code', component: () => import(/* webpackChunkName: "create-message" */ './create_message.vue') },
  { path: '/message/read/:occasion/:recipient/:code', component: () => import(/* webpackChunkName: "message-read" */ './message_read.vue') },
  { path: '/message/:code', component: () => import(/* webpackChunkName: "create-message" */ './create_message.vue') },
  { path: '/c/:shortCode', component: () => import(/* webpackChunkName: "create-message" */ './create_message.vue') },
  { path: '/message/read/:code', component: () => import(/* webpackChunkName: "message-read" */ './message_read.vue') },
  { path: '/card/:occasion/:recipient/:code/:admin_code', component: () => import(/* webpackChunkName: "finish-card" */ './finish_card.vue'), meta: { hideHeader: true } },
  { path: '/card/:code/:admin_code', component: () => import(/* webpackChunkName: "finish-card" */ './finish_card.vue'), meta: { hideHeader: true } },
  { path: '/plane', component: () => import(/* webpackChunkName: "paper-plane" */ './paper_plane_animation.vue') },
  { path: '/loader', component: () => import(/* webpackChunkName: "loader" */ './loader.vue') },
  { path: '/metrics', component: () => import(/* webpackChunkName: "metrics" */ './metrics.vue') },
  { path: '/faq', component: () => import(/* webpackChunkName: "faq" */ './faq.vue') },
  { path: '/fr/faq', component: () => import(/* webpackChunkName: "faq" */ './faq.vue') },
  { path: '/es/faq', component: () => import(/* webpackChunkName: "faq" */ './faq.vue') },
  { path: '/design-system', component: () => import(/* webpackChunkName: "design-system" */ './design_system.vue'), meta: { hideHeader: true } },
  { path: '/moderation', component: () => import(/* webpackChunkName: "moderation" */ './moderation.vue') },
  { path: '/not-found', name: 'not-found', component: () => import(/* webpackChunkName: "not-found" */ './not_found.vue') },
  { path: '/admin', component: () => import(/* webpackChunkName: "admin" */ './admin/index.vue'), children: [
    { path: '', component: () => import(/* webpackChunkName: "admin-dashboard" */ './admin/dashboard.vue') },
    { path: 'cards', component: () => import(/* webpackChunkName: "admin-cards" */ './admin/cards.vue') },
    { path: 'cards/:id', component: () => import(/* webpackChunkName: "admin-card-detail" */ './admin/card_detail.vue') },
    { path: 'data-dashboard', component: () => import(/* webpackChunkName: "admin-data-dashboard" */ './admin/data_dashboard.vue') },
    { path: 'messages', component: () => import(/* webpackChunkName: "admin-messages" */ './admin/messages.vue') },
    { path: 'tracked-participants', component: () => import(/* webpackChunkName: "admin-tracked-participants" */ './admin/tracked_participants.vue') },
    { path: 'images', component: () => import(/* webpackChunkName: "admin-images" */ './admin/images.vue') },
    { path: 'card-generations', component: () => import(/* webpackChunkName: "admin-card-generations" */ './admin/card_generations.vue') },
    { path: 'users', component: () => import(/* webpackChunkName: "admin-users" */ './admin/users.vue') },
    { path: 'top-users', component: () => import(/* webpackChunkName: "admin-top-users" */ './admin/top_users.vue') },
    { path: 'themes/:id/edit', component: () => import(/* webpackChunkName: "admin-theme-edit" */ './admin/theme_edit.vue') },
    { path: 'themes/new', component: () => import(/* webpackChunkName: "admin-theme-create" */ './admin/theme_create.vue') },
    { path: 'themes', component: () => import(/* webpackChunkName: "admin-themes" */ './admin/themes.vue') },
    { path: 'pdf-generation-logs', component: () => import(/* webpackChunkName: "admin-pdf-generation-logs" */ './admin/pdf_generation_logs.vue') },
    { path: 'email-logs', component: () => import(/* webpackChunkName: "admin-email-logs" */ './admin/email_logs.vue') },
    { path: 'email-bounces', component: () => import(/* webpackChunkName: "admin-email-bounces" */ './admin/email_bounces.vue') },
    { path: 'email-previews', component: () => import(/* webpackChunkName: "admin-email-previews" */ './admin/email_previews.vue') },
    { path: 'pdf-generation-attempts', component: () => import(/* webpackChunkName: "admin-pdf-generation-attempts" */ './admin/pdf_generation_attempts.vue') },
    { path: 'message-reports', component: () => import(/* webpackChunkName: "admin-message-reports" */ './admin/message_reports.vue') }
  ] },
  { path: '/:pathMatch(.*)*', redirect: '/not-found' },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.onError((error) => {
  const message = error && error.message ? error.message : ''
  const isChunkLoadError = /Loading chunk .* failed|ChunkLoadError|Importing a module script failed/i.test(message)
  if (!isChunkLoadError || typeof window === 'undefined') return

  const reloadKey = 'sharewell:chunk-reload-attempted'
  if (window.sessionStorage.getItem(reloadKey) === '1') return
  window.sessionStorage.setItem(reloadKey, '1')
  window.location.reload()
})

setupAutoAdsManager(router)

// auth guard
router.beforeEach(async (to, from, next) => {
  const authRoutes = ['/signin', '/signup', '/forgot-password']
  if (authRoutes.includes(to.path)) {
    try {
      const { data } = await axios.get('/auth/me')
      if (data.user) return next('/my-cards')
    } catch {
      /* not logged in — continue */
    }
  }
  next()
})

router.afterEach(() => {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem('sharewell:chunk-reload-attempted')
})

const app = createApp(App)

const head = createHead()
app.use(head)
app.use(VueDOMPurifyHTML)

app.use(router).use(i18n)

router.isReady().then(() => {
  app.mount('#app')
})
